import axios from 'axios';
import store from '../redux/store';
import { USER_LOGOUT } from '../redux/constants/userConstants';

const HIDDEN_ALERT_IDS_STORAGE_KEY = 'home_hidden_alert_ids_v1';
const HIDDEN_ALERT_WATCHERS_STORAGE_KEY = 'home_hidden_alert_watchers_v1';
const NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY = 'network_hidden_alert_keys_v1';
const NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY = 'network_advanced_hide_rules_v1';

// Normalize FormData bodies sent with a JSON content-type.
//
// Several actions build a FormData object but force Content-Type:
// application/json. axios then sends the multipart bytes under a JSON header —
// with no boundary — and the server cannot read the fields, so e.g. login fails
// with "Username and password are required". Converting the FormData to a plain
// object here lets axios's JSON serializer produce a real JSON body the server
// can parse. Requests that legitimately upload files leave their content-type as
// multipart and are untouched.
axios.interceptors.request.use((config) => {
  const ct =
    (config.headers &&
      (config.headers['Content-Type'] || config.headers['content-type'])) ||
    '';
  if (
    typeof FormData !== 'undefined' &&
    config.data instanceof FormData &&
    String(ct).includes('application/json')
  ) {
    // A body carrying a file (e.g. "Make Known" uploads a CSV) must stay
    // multipart — flattening it to JSON drops the file content. Drop the forced
    // JSON header and let axios send real multipart with a boundary. Only the
    // field-only forms get flattened to a JSON object the server can parse.
    let hasFile = false;
    config.data.forEach((value) => {
      if (
        (typeof File !== 'undefined' && value instanceof File) ||
        (typeof Blob !== 'undefined' && value instanceof Blob)
      ) {
        hasFile = true;
      }
    });
    if (hasFile) {
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    } else {
      const obj = {};
      config.data.forEach((value, key) => {
        obj[key] = value;
      });
      config.data = obj;
    }
  }
  return config;
});

// Create an axios interceptor to handle session expiration
axios.interceptors.response.use(
  (response) => {
    // Check if the response indicates session expiration
    if (response.data && response.data.session_expired) {
      // Clear local storage
      localStorage.removeItem('userInfo');
      localStorage.removeItem(HIDDEN_ALERT_IDS_STORAGE_KEY);
      localStorage.removeItem(HIDDEN_ALERT_WATCHERS_STORAGE_KEY);
      localStorage.removeItem(NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY);
      localStorage.removeItem(NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY);
      // Dispatch logout action to clear Redux state
      store.dispatch({ type: USER_LOGOUT });
      // Redirect to login page
      window.location.href = '/#/login';
      
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }
    return response;
  },
  (error) => {
    // A 401 is how the backend signals an expired or missing session — the JWT
    // in the httpOnly cookie is gone or past its expiry. The old Flask backend
    // returned 200 with session_expired:true, which the success handler above
    // caught; the new backend uses the HTTP status, so the redirect has to
    // happen here too or a timed-out session just leaves the user on a dead page.
    const status = error && error.response && error.response.status;
    const url = (error && error.config && error.config.url) || '';
    const onLoginPage = window.location.hash.indexOf('/login') !== -1;
    const isLoginRequest = url.indexOf('/api/login') !== -1;
    if (status === 401 && !onLoginPage && !isLoginRequest) {
      localStorage.removeItem('userInfo');
      localStorage.removeItem(HIDDEN_ALERT_IDS_STORAGE_KEY);
      localStorage.removeItem(HIDDEN_ALERT_WATCHERS_STORAGE_KEY);
      localStorage.removeItem(NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY);
      localStorage.removeItem(NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY);
      store.dispatch({ type: USER_LOGOUT });
      window.location.href = '/#/login';
    }
    return Promise.reject(error);
  }
);

export default axios;
